import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import Profile from "../../assets/images/profile.png";
import BlockIcon from "../../assets/images/icons/block-icon.svg";
import FilterTrigger from "../../commons/FilterTrigger/FilterTrigger";
import { FaSyncAlt } from "react-icons/fa";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { DocumentViewCard } from "../../commons/DocumentCard/DocumentCard";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import DateRangeFilterMenu from "../../commons/DateRangeFilterMenu/DateRangeFilterMenu";
import { Col, Row } from "react-bootstrap";
import { UI_CONSTANTS, getOrderStatusLabel } from "../../utils/constants";
import TablePagination from "../../commons/Pagination/TablePagination";

const formatDob = (dob) => {
  if (!dob) return "N/A";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
    const [year, month, day] = dob.split("-");
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
  }
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return dob;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const DRIVER_DETAIL_FIELDS = [
  { key: "email", label: "Email", getValue: ({ driver }) => driver?.email },
  {
    key: "phone",
    label: "Mobile Number",
    getValue: ({ driver }) =>
      driver?.country_code || driver?.phone
        ? `${driver?.country_code ? `+${driver.country_code} ` : ""}${driver?.phone || ""}`.trim()
        : "",
  },
  { key: "dob", label: "Date Of Birth", getValue: ({ driver }) => formatDob(driver?.dob) },
  { key: "country", label: "Country", getValue: ({ address }) => address?.country },
  { key: "house_no", label: "House/ Flat No", getValue: ({ address }) => address?.house_no },
  { key: "landmark", label: "Landmark", getValue: ({ address }) => address?.landmark },
  { key: "state", label: "State", getValue: ({ address }) => address?.state },
  { key: "pincode", label: "Pincode", getValue: ({ address }) => address?.pincode },
  { key: "full_address", label: "Full Address", getValue: ({ address }) => address?.full_address },
  { key: "latitude", label: "Latitude", getValue: ({ address }) => address?.latitude },
  { key: "longitude", label: "Longitude", getValue: ({ address }) => address?.longitude },
];

const DRIVER_VEHICLE_FIELDS = [
  { key: "license_number", label: "License Number", getValue: ({ data }) => data?.license_number },
  { key: "vehicle_number", label: "Vehicle Registration", getValue: ({ data }) => data?.vehicle_number },
];

const DRIVER_DOCUMENTS = [
  { key: "government_id", title: "Government Issued Id", label: "Government Issued Id Document" },
  { key: "driving_license", title: "Driver License", label: "Driver License Document" },
  { key: "work_permit", title: "Work Permit", label: "Work Permit Document" },
];

const DRIVER_VIEW_RETURN_TO_KEY = "driverViewReturnTo";
const DRIVER_VIEW_RETURN_LIST_STATE_KEY = "driverViewReturnListState";
const ORDERS_PAGE_LIMIT = 10;

const formatOrderDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDistance = (value) => {
  if (value == null || value === "") return "N/A";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return String(value).trim();
  return `${numericValue} Mil`;
};

const formatCurrency = (value) => {
  if (value == null || value === "") return "£0";
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return `£${value}`;
  return `£${numericValue.toFixed(2)}`;
};

const parseDriverOrdersResponse = (payload) => {
  if (!payload) {
    return { list: [], totalPages: 1 };
  }

  if (Array.isArray(payload)) {
    return { list: payload, totalPages: 1 };
  }

  if (Array.isArray(payload.data)) {
    const pagination = payload.pagination || {};
    return {
      list: payload.data,
      totalPages: Number(pagination.totalPages || pagination.total_pages || 1) || 1,
    };
  }

  const list = payload?.result || payload?.orders || [];
  const pagination = payload?.pagination || {};
  const totalPages = pagination?.totalPages || pagination?.total_pages || payload?.totalPages || 1;

  return {
    list: Array.isArray(list) ? list : [],
    totalPages: Number(totalPages) || 1,
  };
};

const getDriverOrderId = (order) => order?.order_number || "N/A";

const getDriverOrderDistance = (order) => formatDistance(order?.delivery_distance);

const getDriverOrderPay = (order) => formatCurrency(order?.delivery_charge);

const getDriverOrderAmount = (order) => formatCurrency(order?.amount_to_pay);

const getDriverOrderDate = (order) => formatOrderDate(order?.createdAt);

const getDriverOrderStatus = (order) => getOrderStatusLabel(order?.order_status);

function ViewDriver() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const returnToRef = useRef(null);
  const returnListStateRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);

  const getDriverDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await handleApiCall(
        "get",
        `${endpoints.getDriverDetail}?driver_id=${id}`
      );

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setData(response?.data?.data);
      }
    } catch (error) {
      toast.error("Failed to fetch driver details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      getDriverDetail();
    }
  }, [id, getDriverDetail]);

  const getDriverOrders = useCallback(async () => {
    if (!id) return;

    setOrdersLoading(true);
    try {
      const params = new URLSearchParams({
        driver_id: id,
        page: String(page),
        limit: String(ORDERS_PAGE_LIMIT),
      });

      if (appliedStartDate && appliedEndDate) {
        params.set("startDate", appliedStartDate);
        params.set("endDate", appliedEndDate);
      }

      const response = await handleApiCall(
        "get",
        `${endpoints.getDriverOrderDeliveryList}?${params.toString()}`
      );

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const { list, totalPages: pages } = parseDriverOrdersResponse(response?.data?.data);
        setOrders(list);
        setTotalPages(pages);
      } else {
        setOrders([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching driver orders:", error);
      setOrders([]);
      setTotalPages(1);
    } finally {
      setOrdersLoading(false);
    }
  }, [id, page, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    getDriverOrders();
  }, [getDriverOrders]);

  const handleApplyDateFilter = (start, end) => {
    setAppliedStartDate(start);
    setAppliedEndDate(end);
    setPage(1);
    setOpen(false);
  };

  const handleResetDateFilter = () => {
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setPage(1);
    setOpen(false);
  };

  useEffect(() => {
    if (location.state?.from) {
      returnToRef.current = location.state.from;
      returnListStateRef.current = location.state.listState ?? null;
      sessionStorage.setItem(DRIVER_VIEW_RETURN_TO_KEY, location.state.from);
      if (location.state.listState) {
        sessionStorage.setItem(
          DRIVER_VIEW_RETURN_LIST_STATE_KEY,
          JSON.stringify(location.state.listState)
        );
      } else {
        sessionStorage.removeItem(DRIVER_VIEW_RETURN_LIST_STATE_KEY);
      }
      return;
    }

    returnToRef.current = null;
    returnListStateRef.current = null;
    sessionStorage.removeItem(DRIVER_VIEW_RETURN_TO_KEY);
    sessionStorage.removeItem(DRIVER_VIEW_RETURN_LIST_STATE_KEY);
  }, [location.state]);

  const driver = data?.driver_id;
  const address = driver?.address?.[0];
  const isActive = Number(driver?.status) === 1;

  const renderFieldCard = (key, label, value) => (
    <div className="col-md-4 p-2" key={key}>
      <div className="rounded-14 border p-3">
        <strong>{label}</strong>
        <p className="mb-0 mt-2">{value || "N/A"}</p>
      </div>
    </div>
  );

  const handleBack = () => {
    const from =
      returnToRef.current ||
      location.state?.from ||
      sessionStorage.getItem(DRIVER_VIEW_RETURN_TO_KEY);

    if (from) {
      const listState =
        returnListStateRef.current ||
        location.state?.listState ||
        (() => {
          const stored = sessionStorage.getItem(DRIVER_VIEW_RETURN_LIST_STATE_KEY);
          if (!stored) return undefined;
          try {
            return JSON.parse(stored);
          } catch {
            return undefined;
          }
        })();

      sessionStorage.removeItem(DRIVER_VIEW_RETURN_TO_KEY);
      sessionStorage.removeItem(DRIVER_VIEW_RETURN_LIST_STATE_KEY);

      navigate(from, {
        state: listState ? { listState } : undefined,
      });
      return;
    }

    navigate("/driver-managment");
  };

  if (loading && !data) {
    return (
      <div className="container-scroller">
        <Header />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper text-center py-5">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">View Driver</h3>
            </div>
            <div>
              <Row>
                <Col md="3" className="mt-3">
                  <button
                    type="button"
                    className="btn btn-default rounded-lg"
                    onClick={handleBack}
                  >
                    Back
                  </button>
                </Col>
                <Col md="6" className="text-center">
                  <span className="font-weight-600">Monthly Income:</span> £{data?.monthly_income ?? "0"}
                  <span className="mx-3">|</span>
                  <span className="font-weight-600">Charges/Mile:</span> £{data?.charges_per_mile ?? "0"}
                </Col>
                <Col md="3" className="text-end">
                  {id && (
                    <Link
                      to={`/driver-managment/view-driver/${id}/wallet-ledger?driver_id=${driver?._id || id}`}
                      state={{
                        driverName: driver?.full_name || "Driver",
                        from: location.pathname,
                      }}
                      className="btn btn-primary rounded-lg px-4"
                    >
                      View Ledger
                    </Link>
                  )}
                </Col>
              </Row>

              <div className="profile-card pt-3 mt-n2">
                <img
                  src={driver?.image || Profile}
                  alt={driver?.full_name || "Driver"}
                  className="profile-img mb-3"
                  style={{ objectFit: "cover" }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = Profile;
                  }}
                />
                <h4 className="fw-bold mb-0">{driver?.full_name || "N/A"}</h4>
                <button
                  type="button"
                  className={`btn status-badge-btn ${isActive ? "btn-success" : "btn-secondary"} mt-2 rounded-lg px-3`}
                  disabled
                  aria-disabled="true"
                >
                  <img src={BlockIcon} className="mr-2" alt="Status" />
                  {isActive ? "Active" : "Inactive"}
                </button>
              </div>
            </div>
            <div className="border rounded-14 py-2 px-3 mt-5">
              <h4 className="px-2 pt-2 mb-0">DRIVER DETAILS</h4>
              <div className="row mx-n2">
                <div className="col-md-4 p-2">
                  <div className="rounded-14 border p-3">
                    <strong>Driver ID</strong>
                    <p className="mb-0 mt-2">#{driver?._id?.slice(-8) || data?._id?.slice(-8) || "N/A"}</p>
                  </div>
                </div>
                {DRIVER_DETAIL_FIELDS.map((field) =>
                  renderFieldCard(field.key, field.label, field.getValue({ driver, address }))
                )}
                <div className="col-md-4 p-2">
                  <div className="rounded-14 border p-3">
                    <strong>Driver Type</strong>
                    <p className="mb-0 mt-2">
                      {Number(data?.agent_type) === 1 ? "In-House" : "External"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <h4 className="mt-4 mb-0 pt-2">VEHICLE DETAILS</h4>
            <div className="border rounded-14 py-2 px-3 mt-3">
              <div className="row mx-n2">
                {DRIVER_VEHICLE_FIELDS.map((field) =>
                  renderFieldCard(field.key, field.label, field.getValue({ data }))
                )}
              </div>
            </div>

            <h4 className="mt-4 mb-0 pt-2">DOCUMENTS</h4>
            <div className="border rounded-14 py-2 px-3 mt-3">
              <div className="row mx-n2">
                {DRIVER_DOCUMENTS.map((doc) => (
                  <DocumentViewCard
                    key={doc.key}
                    title={doc.title}
                    documentLabel={doc.label}
                    url={data?.[doc.key]}
                  />
                ))}
              </div>
            </div>
            <div className="mb-4 px-0 py-2 border mt-4 p-3 rounded-14">
              <div className="card-heading d-flex align-items-center pb-3">
                <h4 className="my-0">DRIVER ORDERS</h4>
                <div className="d-flex align-items-center ml-auto">
                  <button
                    type="button"
                    className="btn btn-default rounded-lg mr-2 d-flex align-items-center justify-content-center"
                    onClick={() => getDriverOrders()}
                    disabled={ordersLoading}
                    title="Reload"
                    aria-label="Reload"
                  >
                    <FaSyncAlt />
                  </button>
                  <div className="dropdown text-end w-auto mr-1">
                    <FilterTrigger
                      variant="icon"
                      bordered={false}
                      active={Boolean(appliedStartDate && appliedEndDate)}
                      onClick={() => setOpen(!open)}
                    />
                    <div
                      className={`dropdown-menu dropdown-menu-end rounded-14 border-0 shadow-lg p-4 ${
                        open ? "show" : ""
                      }`}
                      style={{ top: "-150px", right: "40px" }}
                    >
                      <DateRangeFilterMenu
                        isOpen={open}
                        appliedFrom={appliedStartDate}
                        appliedTo={appliedEndDate}
                        onApply={handleApplyDateFilter}
                        onReset={handleResetDateFilter}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim">
                    <thead>
                      <tr>
                        <th width="150">Order ID</th>
                        <th width="150">Total Distance</th>
                        <th>Total Driver Pay</th>
                        <th>Payment Amount</th>
                        <th>Date</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordersLoading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="sr-only">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : orders.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      ) : (
                        orders.map((order) => (
                          <tr key={order._id}>
                            <td className="text-left">#{getDriverOrderId(order)}</td>
                            <td>{getDriverOrderDistance(order)}</td>
                            <td>{getDriverOrderPay(order)}</td>
                            <td>{getDriverOrderAmount(order)}</td>
                            <td>{getDriverOrderDate(order)}</td>
                            <td className="text-center">{getDriverOrderStatus(order)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewDriver;
